import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { getDayName } from "./tableAvailabilityUtils";

export interface ImpactedReservation {
  id: string;
  date: string;
  dayOfWeek: number;
  dayName: string;
  startTime: string;
  numberOfPeople: number;
  customerName: string;
  impactReason: 'weekday' | 'specific_date';
  tableType: 'single' | 'combined';
}

export interface AvailabilityImpact {
  hasImpactedReservations: boolean;
  impactedReservations: ImpactedReservation[];
}

/**
 * Vérifie l'impact d'une modification de disponibilité hebdomadaire sur les réservations futures
 */
export async function checkWeekdayAvailabilityImpact(
  tableId: string,
  newAvailableDays: number[],
  establishmentId?: string
): Promise<AvailabilityImpact> {
  try {
    // 1. Récupérer les tables combinées contenant cette table
    let combinedQuery = supabase
      .from('combined_table_tables')
      .select('combined_table_id')
      .eq('table_id', tableId);

    if (establishmentId) {
      combinedQuery = combinedQuery.eq('establishment_id', establishmentId);
    }

    const { data: combinedTableData } = await combinedQuery;
    
    const combinedIds = combinedTableData?.map(ct => ct.combined_table_id) || [];

    // 2. Construire la condition OR pour récupérer toutes les réservations concernées
    let query = supabase
      .from('reservations')
      .select(`
        id,
        date,
        start_time,
        number_of_people,
        table_id,
        combined_table_id,
        customers!inner (
          first_name,
          last_name
        )
      `)
      .gte('date', format(new Date(), 'yyyy-MM-dd'))
      .neq('status', 'annulé');

    if (establishmentId) {
      query = query.eq('establishment_id', establishmentId);
    }

    // Construire la condition OR manuellement
    if (combinedIds.length > 0) {
      query = query.or(`table_id.eq.${tableId},combined_table_id.in.(${combinedIds.join(',')})`);
    } else {
      query = query.eq('table_id', tableId);
    }

    const { data: reservations, error } = await query;

    if (error) {
      console.error('Error fetching reservations:', error);
      return { hasImpactedReservations: false, impactedReservations: [] };
    }

    if (!reservations || reservations.length === 0) {
      return { hasImpactedReservations: false, impactedReservations: [] };
    }

    // 3. Filtrer les réservations sur des jours qui ne seront plus disponibles
    const impactedReservations: ImpactedReservation[] = reservations
      .filter(r => {
        const dayOfWeek = new Date(r.date).getDay();
        return !newAvailableDays.includes(dayOfWeek);
      })
      .map(r => ({
        id: r.id,
        date: format(new Date(r.date), 'EEEE d MMMM yyyy', { locale: fr }),
        dayOfWeek: new Date(r.date).getDay(),
        dayName: getDayName(new Date(r.date).getDay()),
        startTime: r.start_time,
        numberOfPeople: r.number_of_people,
        customerName: r.customers ? [r.customers.first_name, r.customers.last_name].filter(Boolean).join(' ') || 'Client inconnu' : 'Client inconnu',
        impactReason: 'weekday' as const,
        tableType: r.combined_table_id ? 'combined' as const : 'single' as const,
      }));

    return {
      hasImpactedReservations: impactedReservations.length > 0,
      impactedReservations,
    };
  } catch (error) {
    console.error('Error checking weekday availability impact:', error);
    return { hasImpactedReservations: false, impactedReservations: [] };
  }
}

/**
 * Vérifie l'impact du blocage d'une date spécifique sur les réservations futures
 */
export async function checkSpecificDateImpact(
  tableId: string,
  blockedDate: Date,
  establishmentId?: string
): Promise<AvailabilityImpact> {
  try {
    // 1. Récupérer les tables combinées contenant cette table
    let combinedQuery = supabase
      .from('combined_table_tables')
      .select('combined_table_id')
      .eq('table_id', tableId);

    if (establishmentId) {
      combinedQuery = combinedQuery.eq('establishment_id', establishmentId);
    }

    const { data: combinedTableData } = await combinedQuery;
    
    const combinedIds = combinedTableData?.map(ct => ct.combined_table_id) || [];

    // 2. Construire la condition OR
    const dateStr = format(blockedDate, 'yyyy-MM-dd');
    let query = supabase
      .from('reservations')
      .select(`
        id,
        date,
        start_time,
        number_of_people,
        table_id,
        combined_table_id,
        customers!inner (
          first_name,
          last_name
        )
      `)
      .eq('date', dateStr)
      .neq('status', 'annulé');

    if (establishmentId) {
      query = query.eq('establishment_id', establishmentId);
    }

    if (combinedIds.length > 0) {
      query = query.or(`table_id.eq.${tableId},combined_table_id.in.(${combinedIds.join(',')})`);
    } else {
      query = query.eq('table_id', tableId);
    }

    const { data: reservations, error } = await query;

    if (error) {
      console.error('Error fetching reservations:', error);
      return { hasImpactedReservations: false, impactedReservations: [] };
    }

    if (!reservations || reservations.length === 0) {
      return { hasImpactedReservations: false, impactedReservations: [] };
    }

    // 3. Toutes les réservations de cette date sont impactées
    const impactedReservations: ImpactedReservation[] = reservations.map(r => ({
      id: r.id,
      date: format(new Date(r.date), 'EEEE d MMMM yyyy', { locale: fr }),
      dayOfWeek: new Date(r.date).getDay(),
      dayName: getDayName(new Date(r.date).getDay()),
      startTime: r.start_time,
      numberOfPeople: r.number_of_people,
      customerName: r.customers ? [r.customers.first_name, r.customers.last_name].filter(Boolean).join(' ') || 'Client inconnu' : 'Client inconnu',
      impactReason: 'specific_date' as const,
      tableType: r.combined_table_id ? 'combined' as const : 'single' as const,
    }));

    return {
      hasImpactedReservations: impactedReservations.length > 0,
      impactedReservations,
    };
  } catch (error) {
    console.error('Error checking specific date impact:', error);
    return { hasImpactedReservations: false, impactedReservations: [] };
  }
}
